import {
  AudioTrack,
  isTrackReference,
  type TrackReference,
  type TrackReferenceOrPlaceholder,
  useRoomContext,
  useTracks,
  VideoTrack,
} from '@livekit/components-react'
import { type Participant, type RemoteParticipant, RoomEvent, Track } from 'livekit-client'
import { memo, useEffect, useMemo, useRef, useState } from 'react'
import { type SaverLevel, wantsCamera } from './dataSaver'
import type { MicAccess } from './deviceProbe'
import { report } from './diagnostics'
import { isMeeting, t } from './i18n'
import type { ModerationAction } from './moderation'
import { remoteCameraIssue } from './remoteVideoHealth'
import { isLiveScreenShare, pickScreenShare, SHARE_AUDIO_VOLUME } from './screenShare'
import { InkToolbar } from './surface/InkToolbar'
import { ShareInkLayer } from './surface/ShareInkLayer'
import type { SurfaceApi } from './surface/useSurface'
import { Whiteboard } from './surface/Whiteboard'
import { TileMenu } from './TileMenu'
import type { FloatingReaction, SharePending } from './useClassroomSignals'
import { isParent, isTeacher } from './useClassroomSignals'
import { WaitingScene } from './WaitingScene'

/**
 * The video stage, composed from primitives rather than the VideoConference
 * prefab.
 *
 * The prefab is competent but generic, and it owns its own control bar — which
 * left no room for a teacher toolbar, reactions, or the brand. Composing gives us
 * the tile treatment a classroom needs: who is talking, who is the teacher, and
 * whose hand is up, all legible at a glance on a phone.
 *
 * Layout intent: when someone is screen sharing OR the board is open, that IS
 * the lesson — it takes the stage and faces become a filmstrip.
 */

interface Props {
  hands: Record<string, number>
  /**
   * Rendered on the SENDER's own tile. Reactions used to go to one room-level
   * layer, which meant every 👍 in the class rose from the same spot over the same
   * face and told you nothing about who sent it.
   */
  reactions: FloatingReaction[]
  /** Chooses whose absence to explain while alone in the room. */
  isTeacherView: boolean
  /** Present only for a teacher — students get no moderation affordances. */
  onModerate?: (identity: string, action: ModerationAction) => Promise<void>
  onAsk?: (identity: string, source: 'microphone' | 'camera') => void
  surface: SurfaceApi
  /** Local data-saver level — a tile whose camera WE unsubscribed says so. */
  saverLevel: SaverLevel
  /** Small-class Host exemption — L1 keeps student cameras (see dataSaver). */
  saverKeepStudents: boolean
  /** Peers in saver mode or reporting a weak connection (honesty badges). */
  weakPeers: Record<string, 'saver' | 'poor'>
  /** Teacher-only: ask this student to share their screen. */
  onAskShare?: (identity: string) => void
  /** Identities whose device advertised it can capture a screen. */
  canShare: Record<string, boolean>
  micAccess: Record<string, MicAccess>
  /** Teacher-only: what each asked student has answered so far. */
  sharePending: Record<string, SharePending>
  /** True while ANY share is on the stage — a second ask makes no sense then. */
  shareBusy: boolean
  credential: { code?: string | null; reconnectToken?: string | null }
  /** True while the local self-view should mirror — no background effect is
   * compositing over the raw camera (). */
  selfViewMirrored: boolean
}

/**
 * Teacher-facing reason a participant's microphone is unavailable, or null when it
 * is simply muted / unknown. Kept next to the tile because the tile menu is where
 * a teacher acts on it.
 */
function micReasonFor(access: MicAccess | undefined): string | null {
  switch (access) {
    case 'blocked':
      return t('roster.micBlocked')
    case 'missing':
      return t('roster.micMissing')
    case 'busy':
      return t('roster.micBusy')
    case 'silent':
      return t('roster.micSilent')
    default:
      return null
  }
}

/**
 * Hard cap on concurrently-DECODED video tiles in the non-presenting grid.
 *
 * The decoder-overload failure mode (room-options.ts) is the real reason this
 * exists: at 25+ participants on a mid-range Android, decoding every camera
 * even at the right-sized h180 layer is more than the silicon can sustain,
 * audio breaks up, and the class is over. The fix is to UNMOUNT — not hide —
 * the surplus <VideoTrack>s, because unmounting is what tells adaptiveStream
 * to release the subscription (see the comment at the grid below). The cap
 * targets the non-presenting grid only; the presenting filmstrip is already
 * correct (overflow-x:auto pauses off-strip) and must not change.
 *
 * The selection is documented at `sortCamerasForCap`.
 */
const MAX_CONCURRENT_DECODES = 12

/**
 * How long after someone's last detected speech the cap sort still treats them
 * as "spoke recently" for ranking purposes. The instantaneous ActiveSpeakers
 * list flickers every frame, so ranking on it directly would promote a student
 * who said one word and demote them the instant they go quiet — churning the
 * visible 12 with repeated VideoTrack mount/unmount and stealing the win the
 * F1 cap bought. Holding the rank for this long after the last speech is
 * long enough to absorb a sentence, short enough that a real silence still
 * demotes the speaker. The RING (Tile's `speaking` prop) is unaffected — that
 * is the instantaneous one, by design.
 */
const SPEAK_HOLD_DOWN_MS = 12_000

/** Stable empty array so the reactions prop can be compared by reference. */
const EMPTY_REACTIONS: readonly FloatingReaction[] = Object.freeze(
  [],
) as readonly FloatingReaction[]

/** Stable empty Set so the onTile memo can return the same reference when
 *  faces are hidden — without this the memo would mint a fresh empty Set per
 *  render and the orphan filter would still re-walk reactions needlessly. */
const EMPTY_IDENTITY_SET: ReadonlySet<string> = Object.freeze(
  new Set<string>(),
) as ReadonlySet<string>

/**
 * Shallow set equality used by the ActiveSpeakers subscription (B4) to bail
 * setSpeakingNow when membership is unchanged. Size check first (cheap); then
 * membership (one pass).
 */
function sameIdentitySet(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  if (a.size !== b.size) return false
  for (const id of b) {
    if (!a.has(id)) return false
  }
  return true
}

/**
 * Sort the camera list so the most important participants land in the first
 * MAX_CONCURRENT_DECODES slots. Stable, so ties fall back to the existing
 * useTracks order (the "fill" criterion the audit specified).
 *
 *   1. local user (isLocal)
 *   2. teacher/host (identity prefix)
 *   3. hand raised, oldest first (fair queue)
 *   4. spoke within SPEAK_HOLD_DOWN_MS (NOT the instantaneous ActiveSpeakers —
 *      see SPEAK_HOLD_DOWN_MS for why that distinction is load-bearing)
 *   5. original order (stable sort tie-breaker)
 *
 * The hands map and the recentlySpoke set are read-only inputs from
 * useClassroomSignals and the LiveKit room, respectively — neither is mutated.
 */
function sortCamerasForCap(
  cameras: TrackReferenceOrPlaceholder[],
  hands: Record<string, number>,
  recentlySpoke: ReadonlySet<string>,
): TrackReferenceOrPlaceholder[] {
  return [...cameras].sort((a, b) => {
    const aId = a.participant.identity
    const bId = b.participant.identity
    if (a.participant.isLocal !== b.participant.isLocal) return a.participant.isLocal ? -1 : 1
    const aTeacher = isTeacher(aId)
    const bTeacher = isTeacher(bId)
    if (aTeacher !== bTeacher) return aTeacher ? -1 : 1
    const aHand = hands[aId] ?? 0
    const bHand = hands[bId] ?? 0
    if (Boolean(aHand) !== Boolean(bHand)) return aHand ? -1 : 1
    if (aHand && bHand) return aHand - bHand // older first
    const aSpeak = recentlySpoke.has(aId)
    const bSpeak = recentlySpoke.has(bId)
    if (aSpeak !== bSpeak) return aSpeak ? -1 : 1
    return 0 // stable sort → original order
  })
}

export function Stage({
  hands,
  reactions,
  isTeacherView,
  onModerate,
  onAsk,
  surface,
  saverLevel,
  saverKeepStudents,
  weakPeers,
  onAskShare,
  canShare,
  micAccess,
  sharePending,
  shareBusy,
  credential,
  selfViewMirrored,
}: Props) {
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false },
  )

  // Deterministic across every client (own → teacher → lowest sid): a bare
  // find() let two racing shares put different people on different screens
  // while the ink layer bound to a third opinion.
  const screenShare = pickScreenShare(tracks)
  const boardOpen = !isMeeting() && surface.boardOpen
  // EVERY sharer, not just the picked one: during a two-share race the teacher
  // needs the stop-share item on the LOSING sharer's tile too. P2-1
  // (meet-opt-03): useMemo so the Set is rebuilt only when `tracks` changes,
  // not on every Stage render (which can fire on a chat unread or a hand
  // raise in Classroom). The membership is just the identities of live shares;
  // a fresh Set with the same content is identity-different and would force
  // any downstream `Set.has` to keep doing real work each render.
  const sharingIdentities = useMemo(
    () => new Set(tracks.filter(isLiveScreenShare).map((track) => track.participant.identity)),
    [tracks],
  )
  const cameras = tracks.filter((track) => track.source === Track.Source.Camera)
  // Board owns the stage the same way a screen share does.
  const presenting = Boolean(screenShare) || boardOpen
  const shareId =
    screenShare && isTrackReference(screenShare)
      ? screenShare.publication?.trackSid || `${screenShare.participant.identity}:share`
      : null

  // F1 wiring — one subscription for the whole room, instead of N calls to
  // useIsSpeaking (which would also break the rules of hooks when the
  // participant count varies). Hoisted here so the cap sort can rank by it.
  //
  // TWO sources of "is speaking" are derived here and used in two different
  // places — the distinction is load-bearing:
  //   - `speakingNow`  → the Tile's `speaking` prop → the indicator ring. This
  //     is the INSTANTANEOUS LiveKit active-speaker set; the ring should match
  //     the SDK's own signal exactly.
  //   - `lastSpokeAt`  → `recentlySpokeSet` below → the CAP RANKING tier of
  //     sortCamerasForCap. This is HELD for SPEAK_HOLD_DOWN_MS after the last
  //     detected speech so a one-word student does not flicker in and out of
  //     the visible 12 and thrash the <VideoTrack> mounts. The brief calls
  //     this "convert oscillation into a slow fade".
  const room = useRoomContext()
  const [speakingNow, setSpeakingNow] = useState<ReadonlySet<string>>(() => new Set())
  const [lastSpokeAt, setLastSpokeAt] = useState<ReadonlyMap<string, number>>(() => new Map())
  useEffect(() => {
    if (!room) return
    const sync = () => {
      const now = Date.now()
      const current = new Set(room.activeSpeakers.map((p) => p.identity))
      // B4 (meet-opt-03) — bail setSpeakingNow when membership is unchanged.
      // ActiveSpeakersChanged fires on every active-speaker update from the
      // SDK (multiple times per second in a talkative class); without the
      // bail, setSpeakingNow always mints a fresh Set and Stage re-renders,
      // then every Tile's `speaking` prop "changes" by reference (even though
      // the boolean is the same) and the Tile memo cannot use the reference
      // change to skip — it has to fall back to its comparator. Keeping the
      // SAME Set reference on a no-op tick is what lets the memo do the
      // cheapest possible thing: return prev without invoking the comparator.
      setSpeakingNow((prev) => (sameIdentitySet(prev, current) ? prev : current))
      setLastSpokeAt((prev) => {
        const next = new Map(prev)
        // Stamp currently-speaking identities with the freshest timestamp.
        for (const id of current) next.set(id, now)
        // Opportunistic prune: entries older than the hold-down window do not
        // affect the sort anymore, and carrying them costs nothing but clarity.
        for (const [id, at] of next) {
          if (now - at > SPEAK_HOLD_DOWN_MS) next.delete(id)
        }
        return next
      })
    }
    sync()
    room.on(RoomEvent.ActiveSpeakersChanged, sync)
    // Drop a leaver so their slot in the hold-down map is freed immediately,
    // not whenever the next ActiveSpeakersChanged tick happens to revisit
    // their identity (it won't, for someone who has left).
    const onDisconnected = (participant: RemoteParticipant) => {
      setLastSpokeAt((prev) => {
        if (!prev.has(participant.identity)) return prev
        const next = new Map(prev)
        next.delete(participant.identity)
        return next
      })
    }
    room.on(RoomEvent.ParticipantDisconnected, onDisconnected)
    return () => {
      room.off(RoomEvent.ActiveSpeakersChanged, sync)
      room.off(RoomEvent.ParticipantDisconnected, onDisconnected)
    }
  }, [room])

  // The "spoke within SPEAK_HOLD_DOWN_MS" set, used by the cap sort. Recomputed
  // when lastSpokeAt changes AND on a 2s tick so the hold-down actually
  // EXPIRES in a quiet class — without the tick, a one-word speaker who then
  // goes silent would stay "recent" until the next ActiveSpeakersChanged event
  // bumped the memo. The tick is a 2s grain on a 12s window: the demotion
  // happens within ±2s of the true 12s mark, and the only cost is one cheap
  // O(n) iteration over lastSpokeAt every 2s. setInterval, not requestAnimation
  // Frame, because this is wall-clock time, not visual animation.
  //
  // B3 (meet-opt-03) — the tick is GATED on cameras.length > N. In a 1:1 class
  // (or any class at or below the decode cap) the cap is never engaged, the
  // sort returns cameras as-is, and the hold-down is irrelevant. Re-rendering
  // Stage every 2s forever on every device was the regression I added in
  // meet-opt-02 to make the hold-down actually expire — gating it here costs
  // nothing for the large class (already re-rendering per event) and removes
  // a permanent background re-render on every small class. The dep is the
  // primitive `cameras.length` so the effect re-evaluates when the count
  // crosses the threshold in either direction.
  const [speakTick, setSpeakTick] = useState(0)
  useEffect(() => {
    if (cameras.length <= MAX_CONCURRENT_DECODES) return
    const id = window.setInterval(() => setSpeakTick((t) => t + 1), 2_000)
    return () => window.clearInterval(id)
  }, [cameras.length])
  const recentlySpokeSet = useMemo(() => {
    // Mirror the B3 gate: below the cap the set is never consumed (the sort
    // returns cameras directly), so return a stable empty Set and stop
    // recomputing on lastSpokeAt / speakTick churn. When the gate opens again
    // this memo re-runs against the current data.
    if (cameras.length <= MAX_CONCURRENT_DECODES) return EMPTY_IDENTITY_SET
    const now = Date.now()
    const set = new Set<string>()
    for (const [id, at] of lastSpokeAt) {
      if (now - at <= SPEAK_HOLD_DOWN_MS) set.add(id)
    }
    return set
  }, [cameras.length, lastSpokeAt, speakTick])

  // F2 wiring — one pass over the reactions list, instead of a per-tile filter
  // scan that turns into O(n·m) at n = cameras and m = reactions. Built once
  // per render; the per-tile lookup is then O(1) AND keeps the same array
  // reference, which the memoized Tile below compares to skip re-renders.
  const reactionsByIdentity = useMemo(() => {
    const map = new Map<string, FloatingReaction[]>()
    for (const reaction of reactions) {
      const list = map.get(reaction.from)
      if (list) list.push(reaction)
      else map.set(reaction.from, [reaction])
    }
    return map
  }, [reactions])

  // Bind ink to this share publication so strokes from a previous share are dropped.
  const setShareId = surface.setShareId
  useEffect(() => {
    setShareId(shareId)
  }, [shareId, setShareId])

  // "Hide faces": while presenting, the filmstrip can be collapsed so the
  // share owns the whole stage — the first cohort's teachers called the share
  // window too small. A while-presenting choice only: when the share ends the
  // grid IS the class again and must come back on its own.
  const [facesHidden, setFacesHidden] = useState(false)
  useEffect(() => {
    if (!presenting) setFacesHidden(false)
  }, [presenting])

  // Fullscreen on the share CONTAINER (not the <video>), so the ink layer and
  // label ride along. Desktop progressive enhancement: fullscreenEnabled is
  // false where the API cannot fullscreen a div (iOS Safari), and the button
  // simply does not exist there — "hide faces" is the affordance everywhere.
  const shareRef = useRef<HTMLDivElement>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  useEffect(() => {
    const onChange = () => setIsFullscreen(Boolean(document.fullscreenElement))
    document.addEventListener('fullscreenchange', onChange)
    return () => document.removeEventListener('fullscreenchange', onChange)
  }, [])
  const canFullscreen = typeof document !== 'undefined' && Boolean(document.fullscreenEnabled)
  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      void document.exitFullscreen().catch(() => undefined)
    } else if (shareRef.current) {
      // Rejection (denied gesture, transient state) just leaves the normal
      // layout — there is nothing useful to tell a child about it.
      void shareRef.current.requestFullscreen().catch(() => undefined)
    }
  }

  // A reaction whose sender has no tile — they left in the second it was in
  // flight, or the filmstrip is collapsed — would otherwise just disappear, so
  // it falls back to the stage. With faces hidden EVERY reaction is an orphan:
  // the tiles that would have carried them are not mounted, and a child's 👍
  // silently vanishing is worse than one that rises over the shared screen.
  //
  // After F1 the overflow is name-only (no <VideoTrack>, no float layer for
  // reactions), so a reaction from someone past the cap is also "orphan" and
  // should rise over the stage, not silently disappear.
  const tilesMounted = !(presenting && facesHidden)

  // F1 selection — at or below the cap, no sort and no overflow (the sort
  // returns the same reference, so the slice is a no-op). Above the cap, the
  // most important N land in visibleCameras; the rest become NameTiles.
  // The "speaking" tier uses the hold-down set (recentlySpokeSet) rather than
  // the instantaneous one — see SPEAK_HOLD_DOWN_MS for why.
  const sortedCameras = useMemo(
    () =>
      cameras.length <= MAX_CONCURRENT_DECODES
        ? cameras
        : sortCamerasForCap(cameras, hands, recentlySpokeSet),
    [cameras, hands, recentlySpokeSet],
  )
  // Memoised so a fresh array identity is not minted on every Stage render.
  // Classroom re-renders on reactions, hand raises and quality ticks; without
  // this, the slice feeding renderedCameras → onTile recomputed the onTile Set
  // and re-filtered orphans each time, even when the camera set was unchanged.
  const visibleCameras = useMemo(
    () => sortedCameras.slice(0, MAX_CONCURRENT_DECODES),
    [sortedCameras],
  )
  const overflowCameras = useMemo(
    () =>
      sortedCameras.length > MAX_CONCURRENT_DECODES
        ? sortedCameras.slice(MAX_CONCURRENT_DECODES)
        : [],
    [sortedCameras],
  )

  // MUST-2 — the source of truth for what is ACTUALLY on screen in the grid
  // (or filmstrip). Used for BOTH the onTile set (which decides which reactions
  // are orphans) AND the .map below (which decides what is rendered). Mixing
  // the two — onTile built from visibleCameras while the grid .map renders
  // `presenting ? cameras : visibleCameras` — made a 13th-participant reaction
  // during a screen share render TWICE (once on the filmstrip tile, once as a
  // stage orphan). One constant, one truth.
  const renderedCameras = presenting ? cameras : visibleCameras

  const onTile = useMemo(
    () =>
      tilesMounted
        ? new Set(renderedCameras.map((track) => track.participant.identity))
        : EMPTY_IDENTITY_SET,
    [renderedCameras, tilesMounted],
  )
  const orphans = reactions.filter((reaction) => !onTile.has(reaction.from))

  const shareActions = (
    <div className="stage__share-actions">
      {boardOpen && screenShare ? (
        <button
          type="button"
          className="stage__share-action"
          onClick={surface.closeBoard}
          title={t('help.share.board')}
        >
          <span aria-hidden="true">🖥</span> {t('help.share.board')}
        </button>
      ) : null}
      {boardOpen && surface.teacher ? (
        <button
          type="button"
          className="stage__share-action"
          onClick={surface.clearBoard}
          title={t('surface.clearBoard')}
        >
          <span aria-hidden="true">🧽</span> {t('surface.clearShort')}
        </button>
      ) : null}
      <button
        type="button"
        className="stage__share-action"
        onClick={() => setFacesHidden((value) => !value)}
        aria-pressed={facesHidden}
        title={t(facesHidden ? 'share.showFaces' : 'share.hideFaces')}
      >
        <span aria-hidden="true">👥</span> {t(facesHidden ? 'room.showFaces' : 'room.hideFaces')}
      </button>
      {/* Fullscreen is for the SHARE only: Excalidraw renders its popovers in
          portals outside this container, and a fullscreened board would hide
          its own menus. */}
      {!boardOpen && canFullscreen ? (
        <button
          type="button"
          className="stage__share-action"
          onClick={toggleFullscreen}
          aria-pressed={isFullscreen}
          title={t(isFullscreen ? 'share.exitFullscreen' : 'share.fullscreen')}
        >
          <span aria-hidden="true">⛶</span>{' '}
          {t(isFullscreen ? 'share.exitFullscreenShort' : 'share.fullscreenShort')}
        </button>
      ) : null}
    </div>
  )

  return (
    <div className={`stage ${presenting ? 'stage--presenting' : ''}`}>
      {boardOpen ? (
        <div className="stage__share stage__share--board">
          <Whiteboard
            viewOnly={!surface.teacher}
            scene={surface.boardScene}
            sceneVersion={surface.boardVersion}
            onSceneChange={surface.publishBoardScene}
            onSceneLocal={surface.setBoardSceneLocal}
            error={surface.teacher ? surface.boardError : null}
          />
          <span className="stage__share-label">{t('surface.board')}</span>
          {/* Board tools live ON the board, not in the control bar: with the
              whiteboard open the bar already carries its toggle, Celebrate and
              NC — one more pill wrapped Leave onto its own row. Top-end corner
              is clear of Excalidraw's own chrome (menu top-start, toolbar
              top-centre). */}
          {shareActions}
        </div>
      ) : screenShare ? (
        <div className="stage__share" ref={shareRef}>
          {/* `publication.track` as well as isTrackReference, mirroring the
              camera tile: isTrackReference only means "not a placeholder", so a
              publication that exists without a subscribed track still rendered
              a <VideoTrack> — i.e. a black rectangle that reads as the app
              being broken.
              Scope, honestly: this covers the pre-subscribe window only. A
              track that is subscribed but delivering nothing HAS a `track`, so
              this line will not show for that case — the pin and the
              never-started watchdog in useScreenSharePin are what handle it. */}
          {isTrackReference(screenShare) && screenShare.publication?.track ? (
            <VideoTrack trackRef={screenShare} className="stage__share-video" />
          ) : (
            <p className="stage__share-waiting" role="status">
              {t('surface.shareConnecting')}
            </p>
          )}
          {!isMeeting() && shareId && (surface.inkArmed || surface.strokes.length > 0) ? (
            <ShareInkLayer
              canDraw={surface.teacher && surface.inkArmed}
              tool={surface.inkTool}
              color={surface.inkColor}
              strokes={surface.strokes}
              identity={surface.identity}
              shareId={shareId}
              publishInk={surface.publishInk}
              eraseInk={surface.eraseInk}
            />
          ) : null}
          {!isMeeting() && surface.teacher && surface.inkArmed ? (
            <InkToolbar
              tool={surface.inkTool}
              color={surface.inkColor}
              onTool={surface.setInkTool}
              onColor={surface.setInkColor}
              onUndo={surface.undoInk}
              onClear={surface.clearInk}
            />
          ) : null}
          <span className="stage__share-label">
            <bdi dir="auto">{screenShare.participant.name || screenShare.participant.identity}</bdi>
            {' · '}
            {t('room.presenting')}
            {!isMeeting() && surface.inkArmed ? ` · ${t('surface.annotating')}` : ''}
          </span>
          {shareActions}
        </div>
      ) : null}

      {/* Alone in the room reads as a failed connection unless we say otherwise —
          and on an unreliable Cairo connection that is exactly the wrong guess for
          a child to make. */}
      {cameras.length <= 1 && !presenting ? <WaitingScene teacherView={isTeacherView} /> : null}

      {/* Collapsed tiles UNMOUNT rather than hide: adaptiveStream then pauses
          those camera subscriptions, so hiding faces also gives the link back
          to the share — the same thing the user is trying to buy.
          F1: in the non-presenting grid, beyond the decode cap the <VideoTrack>
          is UNMOUNTED (not CSS-hidden) by swapping the Tile for a NameTile.
          Unmounting is what makes adaptiveStream release the subscription —
          exactly the win the audit was after. The filmstrip is left alone:
          overflow-x:auto already pauses off-strip and the per-tile h180 layers
          are small enough at typical class sizes. */}
      {tilesMounted ? (
        <div
          className={
            presenting
              ? 'filmstrip'
              : overflowCameras.length > 0
                ? 'grid grid--many'
                : `grid grid--${gridSize(cameras.length)}`
          }
        >
          {renderedCameras.map((trackRef) => (
            <Tile
              key={`${trackRef.participant.identity}-${trackRef.source}`}
              trackRef={trackRef}
              handRaisedAt={hands[trackRef.participant.identity]}
              compact={presenting}
              reactions={reactionsByIdentity.get(trackRef.participant.identity) ?? EMPTY_REACTIONS}
              sharing={sharingIdentities.has(trackRef.participant.identity)}
              // MUST-1 — the mic-enabled gate is BACK, and the per-track
              // rationale lives HERE at the call site so a future hoist
              // (this was previously useIsSpeaking(p) inside Tile) cannot
              // lose it again. LiveKit's active-speaker figure is per
              // PARTICIPANT, not per track, so a teacher playing a loud shared
              // video is measured as speaking by the share's audio and the
              // ring would stay lit for the whole clip. The gate is partial
              // and honestly so — an unmuted teacher saying nothing over a loud
              // video still reads as speaking — but it removes the case that
              // lasts for minutes. Mirror what useIsSpeaking was doing: the
              // instantaneous set from ActiveSpeakersChanged AND a
              // mic-enabled check on the participant itself.
              speaking={
                speakingNow.has(trackRef.participant.identity) &&
                trackRef.participant.isMicrophoneEnabled
              }
              micMuted={!trackRef.participant.isMicrophoneEnabled}
              cameraOff={!trackRef.participant.isCameraEnabled}
              onModerate={onModerate}
              onAsk={onAsk}
              saverLevel={saverLevel}
              saverKeepStudents={saverKeepStudents}
              weak={weakPeers[trackRef.participant.identity]}
              onAskShare={onAskShare}
              canShare={Boolean(canShare[trackRef.participant.identity])}
              micAccess={micAccess[trackRef.participant.identity]}
              sharePending={sharePending[trackRef.participant.identity]}
              shareBusy={shareBusy}
              credential={credential}
              selfViewMirrored={selfViewMirrored}
            />
          ))}
          {presenting
            ? null
            : overflowCameras.map((trackRef) => (
                <NameTile
                  key={`name-${trackRef.participant.identity}`}
                  participant={trackRef.participant}
                />
              ))}
        </div>
      ) : null}

      {orphans.length > 0 ? (
        <div className="stage__orphan-reactions" aria-hidden="true">
          {orphans.map((reaction) => (
            <span
              key={reaction.id}
              className="reaction-layer__item"
              style={
                {
                  '--drift': `${reaction.offset}px`,
                  '--peak': reaction.peak,
                  '--spin': `${reaction.spin}deg`,
                  '--dur': `${reaction.duration}s`,
                } as React.CSSProperties
              }
            >
              {reaction.emoji}
            </span>
          ))}
        </div>
      ) : null}

      {/* Audio for every remote participant, kept out of the tile tree so a
          re-render of the grid can never interrupt someone's sound. */}
      <RemoteAudio />
    </div>
  )
}

function RemoteAudio() {
  // ScreenShareAudio too: a teacher sharing a video WITH sound published the
  // audio track, but nothing here ever played it — shares were always silent.
  const audioTracks = useTracks([Track.Source.Microphone, Track.Source.ScreenShareAudio], {
    onlySubscribed: true,
  })
  return (
    <>
      {audioTracks
        .filter((ref): ref is TrackReference => isTrackReference(ref) && !ref.participant.isLocal)
        .map((ref) => (
          // Keyed by track, not participant: a presenter has BOTH a mic and a
          // share-audio track, and identity keys collided the two.
          <AudioTrack
            key={ref.publication.trackSid}
            trackRef={ref as never}
            // Share audio sits a few dB under the voices — see
            // SHARE_AUDIO_VOLUME. Microphones are left at 1: a child who is
            // hard to hear must never be attenuated by us.
            volume={ref.source === Track.Source.ScreenShareAudio ? SHARE_AUDIO_VOLUME : undefined}
          />
        ))}
    </>
  )
}

type TileProps = {
  trackRef: TrackReferenceOrPlaceholder
  handRaisedAt?: number
  compact: boolean
  reactions: readonly FloatingReaction[]
  sharing: boolean
  /** Currently active speaker — passed in from the Stage-level subscription. */
  speaking: boolean
  /** Derived at the call site so the memo comparator can detect changes. */
  micMuted: boolean
  cameraOff: boolean
  onModerate?: (identity: string, action: ModerationAction) => Promise<void>
  onAsk?: (identity: string, source: 'microphone' | 'camera') => void
  saverLevel: SaverLevel
  saverKeepStudents: boolean
  weak?: 'saver' | 'poor'
  onAskShare?: (identity: string) => void
  canShare: boolean
  micAccess?: MicAccess
  sharePending?: SharePending
  shareBusy: boolean
  credential: { code?: string | null; reconnectToken?: string | null }
  /** Mirrors the Stage-level prop onto the local tile only (). */
  selfViewMirrored: boolean
}

/**
 * F2 comparator.
 *
 * The default `===` shallow check fails because useTracks mints a new
 * TrackReference every time it ticks — the trackRef reference would always
 * differ and the memo would never short-circuit. We compare the FIELDS that
 * actually drive the tile's visual output instead:
 *
 * - identity / trackSid / isMuted / Boolean(track) from the trackRef and its
 *   publication (these change when the camera publication changes)
 * - every scalar prop the tile reads, including the speaking/micMuted/cameraOff
 *   booleans derived at the call site (so mic/cam state changes propagate
 *   even though the participant object reference itself is fresh)
 * - the three callbacks, which are stable useCallback refs from Classroom and
 *   `useClassroomSignals` and therefore safe to compare by identity
 *
 * `participant.name` and the `trackRef` reference itself are intentionally
 * NOT compared — a rename in flight is a rare event, and the next change in
 * any other compared prop will refresh the tile.
 */
function tilePropsAreEqual(prev: TileProps, next: TileProps): boolean {
  return (
    prev.trackRef.participant.identity === next.trackRef.participant.identity &&
    prev.trackRef.publication?.trackSid === next.trackRef.publication?.trackSid &&
    prev.trackRef.publication?.isMuted === next.trackRef.publication?.isMuted &&
    Boolean(prev.trackRef.publication?.track) === Boolean(next.trackRef.publication?.track) &&
    prev.handRaisedAt === next.handRaisedAt &&
    prev.compact === next.compact &&
    prev.reactions === next.reactions &&
    prev.sharing === next.sharing &&
    prev.speaking === next.speaking &&
    prev.micMuted === next.micMuted &&
    prev.cameraOff === next.cameraOff &&
    prev.saverLevel === next.saverLevel &&
    prev.saverKeepStudents === next.saverKeepStudents &&
    prev.weak === next.weak &&
    prev.canShare === next.canShare &&
    prev.micAccess === next.micAccess &&
    prev.sharePending === next.sharePending &&
    prev.shareBusy === next.shareBusy &&
    prev.credential === next.credential &&
    prev.selfViewMirrored === next.selfViewMirrored &&
    prev.onModerate === next.onModerate &&
    prev.onAsk === next.onAsk &&
    prev.onAskShare === next.onAskShare
  )
}

function useRemoteCameraHealthIssue(
  video: HTMLVideoElement | null,
  isLocal: boolean,
  intendedOn: boolean,
  track: Track | undefined,
): 'paused' | 'stalled' | null {
  const [issue, setIssue] = useState<'paused' | 'stalled' | null>(null)
  const subscribed = Boolean(track)

  useEffect(() => {
    if (isLocal || !intendedOn || !subscribed || !track) {
      setIssue(null)
      return
    }
    if (!video) return

    let watchingSince = Date.now()
    let lastDecodedAt: number | null = null
    let lastDecoded = 0
    let frameRequest = 0
    const canRafc = typeof video.requestVideoFrameCallback === 'function'
    const canQuality = typeof video.getVideoPlaybackQuality === 'function'
    const markDecoded = () => {
      lastDecodedAt = Date.now()
    }
    const onFrame = () => {
      markDecoded()
      if (canRafc) frameRequest = video.requestVideoFrameCallback(onFrame)
    }
    if (canRafc) frameRequest = video.requestVideoFrameCallback(onFrame)

    const check = () => {
      const now = Date.now()
      if (document.hidden) {
        watchingSince = now
        lastDecodedAt = null
        setIssue((previous) => (previous === null ? previous : null))
        return
      }
      if (canQuality) {
        const decoded = video.getVideoPlaybackQuality().totalVideoFrames
        if (decoded > lastDecoded) {
          lastDecoded = decoded
          markDecoded()
        }
      }
      const next = remoteCameraIssue({
        streamState: track.streamState,
        // A browser without a frame-progress API can still tell us about an SFU
        // pause, but cannot honestly claim a visible frame has stalled.
        lastDecodedAt: canRafc || canQuality ? lastDecodedAt : now,
        watchingSince,
        now,
        intendedOn,
        subscribed,
      })
      setIssue((previous) => (previous === next ? previous : next))
    }

    check()
    const timer = window.setInterval(check, 500)
    return () => {
      window.clearInterval(timer)
      if (canRafc && frameRequest) video.cancelVideoFrameCallback(frameRequest)
    }
  }, [intendedOn, isLocal, subscribed, track, video])

  return issue
}

const Tile = memo(function Tile({
  trackRef,
  handRaisedAt,
  compact,
  reactions,
  sharing,
  speaking,
  micMuted,
  cameraOff,
  onModerate,
  onAsk,
  saverLevel,
  saverKeepStudents,
  weak,
  onAskShare,
  canShare,
  micAccess,
  sharePending,
  shareBusy,
  credential,
  selfViewMirrored,
}: TileProps) {
  const participant = trackRef.participant
  const teacher = isTeacher(participant.identity)
  // A parent joins as a plain Participant, so without this they would be
  // indistinguishable from a student on the tile.
  const parent = isParent(participant.identity)
  // `track` as well as `!isMuted`: a publication the data saver unsubscribed
  // still exists and is not muted — without the track check it rendered as a
  // black <video>, which reads as broken rather than saving.
  const hasVideo =
    isTrackReference(trackRef) &&
    !trackRef.publication?.isMuted &&
    Boolean(trackRef.publication?.track)
  const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>(null)
  const remoteIssue = useRemoteCameraHealthIssue(
    videoElement,
    participant.isLocal,
    !cameraOff,
    isTrackReference(trackRef) ? trackRef.publication?.track : undefined,
  )
  const stalledReported = useRef(false)
  useEffect(() => {
    if (remoteIssue !== 'stalled' || participant.isLocal || stalledReported.current) return
    stalledReported.current = true
    report(credential, {
      stage: 'permissions',
      outcome: 'error',
      permission: `remote_cam=stalled,role=${isTeacher(participant.identity) ? 'teacher' : 'student'}`,
    })
  }, [credential, participant.identity, participant.isLocal, remoteIssue])
  // Camera exists and is on, but WE dropped it to save data — say so.
  const pausedBySaver =
    !hasVideo &&
    isTrackReference(trackRef) &&
    !trackRef.publication?.isMuted &&
    !participant.isLocal &&
    saverLevel > 0 &&
    !wantsCamera(saverLevel, teacher, saverKeepStudents)
  const name = participant.name || participant.identity

  // Only over OTHER people, and never over staff: a teacher has no business
  // muting a co-teacher, and the server refuses it anyway.
  const moderatable = Boolean(onModerate) && !participant.isLocal && !teacher

  return (
    <div
      className={[
        'tile',
        speaking ? 'tile--speaking' : '',
        teacher ? 'tile--teacher' : '',
        compact ? 'tile--compact' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {hasVideo ? (
        <>
          <VideoTrack
            ref={setVideoElement}
            trackRef={trackRef as never}
            // Mirrored for the local participant only: seeing yourself un-mirrored
            // makes people adjust the wrong way. EXCEPT while a background
            // effect composites over the raw camera (): the composited
            // frame — brand logo, readable text — must not read backwards to
            // the teacher. The published track is never mirrored either way; this
            // is self-view only.
            className={`tile__video ${participant.isLocal && selfViewMirrored ? 'tile__video--mirrored' : ''}`}
          />
          {remoteIssue ? (
            <span className="tile__camera-off tile__video-issue" role="status">
              {t('tile.connectionIssue')}
            </span>
          ) : null}
        </>
      ) : (
        <div className="tile__placeholder">
          <Avatar participant={participant} />
          <span className="tile__camera-off">
            {pausedBySaver ? <SignalIcon /> : <CamOffIcon />}
            {t(pausedBySaver ? 'saver.pausedTile' : 'prejoin.cameraOff')}
          </span>
        </div>
      )}

      <div className="tile__footer">
        <span className="tile__name">
          {weak && !participant.isLocal ? (
            <span className="tile__weak" title={t('saver.weakPeer')}>
              <SignalIcon />
              <span className="visually-hidden">{t('saver.weakPeer')}</span>
            </span>
          ) : null}
          {micMuted ? (
            <span className="tile__muted" title={t('room.muted')}>
              <MutedIcon />
              <span className="visually-hidden">{t('room.muted')}</span>
            </span>
          ) : null}
          {/* bdi + min-width:0 so a long Arabic name truncates instead of shoving
              the teacher badge out of the tile. */}
          <bdi dir="auto" className="tile__name-text">
            {name}
          </bdi>
          {participant.isLocal ? <span className="tile__you">{t('room.you')}</span> : null}
        </span>
        {teacher ? (
          <span className="badge badge--teacher">{t('role.teacher')}</span>
        ) : parent ? (
          <span className="badge badge--parent">{t('role.parent')}</span>
        ) : null}
      </div>

      {speaking ? (
        <span className="tile__speaking" aria-hidden="true">
          <i />
          <i />
          <i />
        </span>
      ) : null}

      {reactions.length > 0 ? (
        <div className="tile__reactions" aria-hidden="true">
          {reactions.map((reaction) => (
            <span
              key={reaction.id}
              className="tile__reaction"
              // Every reaction carries its own motion (see FloatingReaction), so no
              // two play the same animation.
              style={
                {
                  '--rise': `${reaction.rise}%`,
                  '--dur': `${reaction.duration}s`,
                } as React.CSSProperties
              }
            >
              <span
                className="tile__reaction-glyph"
                style={
                  {
                    '--drift': `${reaction.offset}px`,
                    '--peak': reaction.peak,
                    '--spin': `${reaction.spin}deg`,
                    '--sway': `${reaction.sway}px`,
                    '--dur': `${reaction.duration}s`,
                  } as React.CSSProperties
                }
              >
                {reaction.emoji}
              </span>
            </span>
          ))}
        </div>
      ) : null}

      {/* One corner, laid out as a row. Both of these used to be pinned to
          top/inset-inline-end 8px independently, so a raised hand covered the
          teacher's menu button completely. */}
      {handRaisedAt || moderatable ? (
        <div className="tile__top-end">
          {handRaisedAt ? (
            <span
              className="tile__hand"
              title={t('room.handRaised')}
              aria-label={t('room.handRaised')}
            >
              ✋
            </span>
          ) : null}
          {moderatable ? (
            <TileMenu
              name={name}
              micMuted={micMuted}
              // Publication truth, not subscription truth: under the local data
              // saver `hasVideo` is false while their camera is genuinely ON —
              // the menu must not offer "ask to turn the camera on" to a child
              // whose camera never went off.
              cameraOff={cameraOff}
              sharing={sharing}
              onModerate={(action) => onModerate!(participant.identity, action)}
              onAsk={(source) => onAsk?.(participant.identity, source)}
              onAskShare={onAskShare ? () => onAskShare(participant.identity) : undefined}
              isParent={parent}
              canShare={canShare}
              micReason={micReasonFor(micAccess)}
              sharePending={sharePending}
              shareBusy={shareBusy}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  )
}, tilePropsAreEqual)

/**
 * F1 overflow tile — the lightweight replacement when a camera sits past the
 * concurrent-decode cap.
 *
 * Deliberately the same `.tile` shape as a normal tile (so the grid layout
 * treats it identically and the existing tile__placeholder / tile__footer
 * styles work without a fork), but:
 *   - no <VideoTrack>, so unmounting this is what releases the subscription
 *   - no useIsSpeaking, so no per-tile room subscription
 *   - no hand raised, no menu, no reactions — a name-only placeholder
 *
 * Not memoized: the parent re-renders infrequently (only on camera/hands/
 * speaking ticks) and this body is pure JSX, so the cost is dominated by the
 * grid's own reconciliation. Memoizing would freeze the role badge and
 * local-participant indicator between renders, which is not what we want.
 */
function NameTile({ participant }: { participant: Participant }) {
  const name = participant.name || participant.identity
  const teacher = isTeacher(participant.identity)
  const parent = isParent(participant.identity)
  return (
    <div className="tile tile--name-only">
      <div className="tile__placeholder">
        <Avatar participant={participant} />
      </div>
      <div className="tile__footer">
        <span className="tile__name">
          {participant.isLocal ? <span className="tile__you">{t('room.you')}</span> : null}
          <bdi dir="auto" className="tile__name-text">
            {name}
          </bdi>
        </span>
        {teacher ? (
          <span className="badge badge--teacher">{t('role.teacher')}</span>
        ) : parent ? (
          <span className="badge badge--parent">{t('role.parent')}</span>
        ) : null}
      </div>
    </div>
  )
}

function Avatar({ participant }: { participant: Participant }) {
  const name = participant.name || participant.identity
  const initials = name
    .replace(/^[A-Za-z]+:/, '') // identities look like "Student:123"
    .split(/[\s_]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()

  return <span className="tile__initials">{initials || '?'}</span>
}

/**
 * Exact buckets rather than a formula. The coarse version put two participants in
 * two columns even on a portrait phone, where each tile became very tall and
 * object-fit: cover cropped most of a teacher's landscape camera; and it left an
 * unexplained empty cell at three. Portrait overrides live in the stylesheet.
 */
function gridSize(count: number): string {
  if (count <= 1) return '1'
  if (count === 2) return '2'
  if (count === 3) return '3'
  if (count === 4) return '4'
  if (count <= 6) return '6'
  if (count <= 9) return '9'
  if (count <= 12) return '12'
  // Above twelve, a fixed template stops making sense: extra tiles landed in
  // implicit rows and squashed the grid. 'many' auto-fits instead.
  return 'many'
}

const CamOffIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
    <path d="M4.3 3 3 4.3 5.7 7H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h12a1 1 0 0 0 .9-.6L19.7 21 21 19.7 4.3 3ZM17 8.5V7a1 1 0 0 0-1-1H9.5l7.5 7.4V13l4 4V6l-4 2.5Z" />
  </svg>
)

const MutedIcon = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor" aria-hidden="true">
    <path d="M15 10.6V6a3 3 0 0 0-5.94-.6l5.94 5.2ZM4.3 3 3 4.3l6 5.3V12a3 3 0 0 0 4.6 2.55l1.4 1.23A5 5 0 0 1 7 12H5a7 7 0 0 0 6 6.92V22h2v-3.08a7 7 0 0 0 3.2-1.35L19.7 20 21 18.7 4.3 3Z" />
  </svg>
)

/** Two-of-four signal bars: universally "weak connection", no text needed. */
const SignalIcon = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor" aria-hidden="true">
    <path d="M3 20h3v-5H3v5Zm5.5 0h3V9h-3v11Z" />
    <path d="M14 20h3V4h-3v16Zm5.5 0h3v-9h-3v9Z" opacity="0.35" />
  </svg>
)
